import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { useBudgetTypes } from '@/features/master-table/hooks';
import { BudgetTypeModel } from '@/features/master-table/models';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { invalidateListMap } from '../../../../_utils/query-keys';
import { ExpenditureCartBaseModel } from '../../../_models/expenditure-cart';
import {
  CreateExpenditureItemParamSchema,
  ExpenditureItemBaseModel,
  PriorReserveBudgetItemMutationParamsSchema,
} from '../../../_models/expenditure-item';
import { ProjectBaseModel } from '../../../_models/project';

export const useCreateExpenditureItem = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof CreateExpenditureItemParamSchema>,
    ) => {
      const {
        projectId,
        subProjectId,
        expenditureCartId,
        expenditureBudgetPurposeConstituentIds,
        ...payload
      } = CreateExpenditureItemParamSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/expenditure-items/`,
        {
          ...payload,
          expenditureBudgetPurposeId:
            expenditureBudgetPurposeConstituentIds?.at(-1) ?? null,
        },
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

const UpdateExpenditureItemParamSchema = CreateExpenditureItemParamSchema.omit({
  budgetTypeId: true,
}).extend({
  id: z.string().uuid(),
});

export const useUpdateExpenditureItem = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateExpenditureItemParamSchema>,
    ) => {
      const {
        id,
        projectId,
        subProjectId,
        expenditureCartId,
        expenditureBudgetPurposeConstituentIds,
        ...payload
      } = UpdateExpenditureItemParamSchema.extend({
        id: z.string().uuid(),
      }).parse(values);

      const res = await axiosPatch(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/expenditure-items/${id}/`,
        {
          ...payload,
          expenditureBudgetPurposeId:
            expenditureBudgetPurposeConstituentIds?.at(-1) ?? null,
        },
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useDeleteExpenditureItem = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      subProjectId,
      expenditureCartId,
    }: {
      id: string;
      projectId: string;
      subProjectId: string;
      expenditureCartId: string;
    }) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/expenditure-items/${id}/`,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useUpsertReserveBudgetItem = () => {
  const { invalidate } = useInvalidateQuery();
  const budgetTypes = useBudgetTypes();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PriorReserveBudgetItemMutationParamsSchema>,
    ) => {
      const {
        projectId,
        subProjectId,
        expenditureCartId,
        budgetTypeId,
        finalizedAmount,
        remark,
      } = PriorReserveBudgetItemMutationParamsSchema.parse(values);
      const project = await axiosGet(`/api/projects/${projectId}/`);
      const fiscalYear = ProjectBaseModel.parse(project).fiscalYear;
      const reserveBudget = budgetTypes.data?.find(
        (budgetType) =>
          budgetType.fiscalYear === fiscalYear &&
          budgetType.type === 'reserve' &&
          budgetType.id === budgetTypeId,
      );
      if (!reserveBudget) {
        throw new Error('target-budget-type-not-exist');
      }
      const expenditureCart = await axiosGet(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/`,
      );
      const exist = ExpenditureCartBaseModel.extend({
        expenditureItems: ExpenditureItemBaseModel.extend({
          budgetType: BudgetTypeModel,
        }).array(),
      })
        .parse(expenditureCart)
        .expenditureItems.find(
          (item) =>
            item.budgetType.type === 'reserve' &&
            item.budgetType.id === budgetTypeId,
        );
      const res = exist
        ? await axiosPatch(
            `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/expenditure-items/${exist.id}/`,
            { finalizedAmount, remark, skipLock: true },
          )
        : await axiosPost(
            `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${expenditureCartId}/expenditure-items/`,
            {
              budgetTypeId: reserveBudget.id,
              finalizedAmount,
              remark,
              skipLock: true,
            },
          );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};
